import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navbar, Nav, Container } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

// Back to Top Button Component
const BackToTopButton = () => {
  const [isVisible, setIsVisible] = useState(false);

  const toggleVisibility = () => {
    if (window.pageYOffset > 300) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    window.addEventListener('scroll', toggleVisibility);
    return () => {
      window.removeEventListener('scroll', toggleVisibility);
    };
  }, []);

  return (
    <button 
      className={`back-to-top ${isVisible ? 'show' : ''}`}
      onClick={scrollToTop}
      aria-label="Back to top"
    >
      ↑
    </button>
  );
};

const testimonials = [
  {
    id: 1,
    text: "\"If you are looking for a professional & efficient web designer then Brian is the person to hire. He delivers on time and meets expectations. Brian worked on a business site as well as a non-for profit that I am involved with and the results are amazing. He listened and produced what I wanted to achieve. I am not tech savvy and what I really appreciated was that he took the time to explain the process and offered suggestions.\"",
    author: "Michelle Diaz",
    title: "V.P. Operations; WDBA, President; GNG Canada"
  },
  {
    id: 2,
    text: "\"We retained Brian to help us redo our website. He was very helpful and professional, with a keen eye for detail. Brian took the time to educate us on how certain technical aspects worked within our site during the entire construction process. We were very impressed with his patience in listening to and implementing all of our ideas, in order to create a website that now accurately represents our brand and image. Perfectly executed and on time. Thank you Brian!\"",
    author: "Reema Juneja",
    title: "CEO"
  },
  {
    id: 3,
    text: "\"Working with Brian is easy and educational. He has a talent of taking my words and turning them into my vision. He is someone who understands the back end of building a website and educating you on what it means to have a safe and secure site. My experience has been so amazing. I have worked with Brian through many changes for my website and continue to hire him regularly. If you are looking for a creative and knowledgeable designer then Brian is your guy! Thanks Brian for creating my vision!\"",
    author: "Penny Hounsome",
    title: "Entrepreneur"
  },
  {
    id: 4,
    text: "\"Brian is efficient and excellent at building and maintaining websites. I have worked with him since 2013 and it has been a pleasure. Great service Brian, keep it up.\"",
    author: "Shannon Hardy",
    title: "Musician"
  },
  {
    id: 5,
    text: "\"Working with Brian was a pleasure. Efficient as well as creative; Brian was able to take the elements of our business, and create an extremely user friendly web site environment. Brian's suggestions and solutions were well thought out, and fit perfectly with our direction and branding.\"",
    author: "Paul Cass",
    title: "Owner, Account4it"
  }
];

function TestimonialsCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const trackRef = useRef(null);
  const intervalRef = useRef(null);
  
  const goToSlide = (index) => {
    if (index < 0) index = testimonials.length - 1;
    else if (index >= testimonials.length) index = 0;
    setCurrentIndex(index);
  };

  useEffect(() => {
    // Auto-advance every 8 seconds
    const timer = setInterval(() => {
      goToSlide(currentIndex + 1);
    }, 8000);
    
    // Cleanup interval on component unmount
    return () => clearInterval(timer);
  }, [currentIndex]);

  useEffect(() => {
    if (trackRef.current) {
      const slideWidth = 100 / testimonials.length;
      trackRef.current.style.transform = `translateX(-${currentIndex * slideWidth}%)`;
    }
  }, [currentIndex]);

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      maxWidth: '800px',
      margin: '0 auto',
      minHeight: '300px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      {testimonials.map((testimonial, index) => (
        <div 
          key={testimonial.id}
          style={{
            position: 'absolute',
            left: '50%',
            transform: index === currentIndex 
              ? 'translateX(-50%) scale(1.02)' 
              : 'translateX(-50%) scale(1)',
            width: '90%',
            maxWidth: '700px',
            padding: '25px',
            background: '#1a252f',
            borderRadius: '8px',
            borderLeft: '3px solid ' + (index === currentIndex ? '#00bcd4' : '#007c91'),
            boxShadow: index === currentIndex
              ? '0 0 10px rgba(0, 180, 220, 0.4), 0 0 20px rgba(0, 100, 200, 0.15)'
              : '0 2px 10px rgba(0, 0, 0, 0.15)',
            opacity: index === currentIndex ? 1 : 0,
            transition: 'all 0.6s ease-in-out',
            filter: index === currentIndex ? 'drop-shadow(0 0 5px rgba(0, 180, 220, 0.5))' : 'none',
            zIndex: index === currentIndex ? 2 : 1,
            pointerEvents: 'none',
            textAlign: 'left'
          }}
        >
          <p style={{
            margin: '0 0 15px 0',
            color: '#e0e0e0',
            fontStyle: 'italic',
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            display: '-webkit-box',
            WebkitLineClamp: 4,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: 1.6,
            textAlign: 'left'
          }}>
            {testimonial.text}
          </p>
          <cite style={{
            display: 'block',
            color: '#00bcd4',
            fontWeight: 'bold',
            fontStyle: 'normal',
            fontSize: '0.95em'
          }}>
            {testimonial.author}<br/>{testimonial.title}
          </cite>
        </div>
      ))}
    </div>
  );
}

function App() {
  const [email, setEmail] = useState('');
  const [isEmailRevealed, setIsEmailRevealed] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const navRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY;
      setIsScrolled(offset > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Obfuscate email on component mount
    const obfuscated = 'david.shreve' + String.fromCharCode(64) + 'gmail.com';
    setEmail(obfuscated);
  }, []);

  const handleEmailClick = (e) => {
    e.preventDefault();
    if (!isEmailRevealed) {
      setEmail('david.shreve@gmail.com');
      setIsEmailRevealed(true);
    } else {
      window.location.href = 'mailto:david.shreve@gmail.com';
    }
  };
  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    const section = document.querySelector(sectionId);
    if (section) {
      const headerOffset = 100; // Fixed offset for the navbar
      const sectionTop = section.offsetTop;
      const offsetPosition = sectionTop - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      
      // Update URL without adding to history
      window.history.pushState(null, '', sectionId);
    }
  };

  return (
    <div className="App">
      <Navbar 
        expand="lg" 
        fixed="top" 
        expanded={expanded}
        onToggle={() => setExpanded(!expanded)}
        ref={navRef}
        className={isScrolled ? 'navbar-scrolled' : ''}
      >
        <Container>
          <Navbar.Brand href="#home">D.B. (Brian) Shreve</Navbar.Brand>
          <Navbar.Toggle aria-controls="responsive-navbar-nav" onClick={() => setExpanded(!expanded)} />
          <Navbar.Collapse id="responsive-navbar-nav" onClick={() => setExpanded(false)}>
            <Nav className="ms-auto">
              <Nav.Link 
                href="#about" 
                className={({ isActive }) => isActive ? 'active' : ''}
                onClick={(e) => scrollToSection(e, '#about')}
              >
                About Me
              </Nav.Link>
              <Nav.Link 
                href="#career" 
                className={({ isActive }) => isActive ? 'active' : ''}
                onClick={(e) => scrollToSection(e, '#career')}
              >
                Career Highlights
              </Nav.Link>
              <Nav.Link 
                href="#focus" 
                className={({ isActive }) => isActive ? 'active' : ''}
                onClick={(e) => scrollToSection(e, '#focus')}
              >
                Today's Focus
              </Nav.Link>
              <Nav.Link 
                href="#personal" 
                className={({ isActive }) => isActive ? 'active' : ''}
                onClick={(e) => scrollToSection(e, '#personal')}
              >
                Personal Side
              </Nav.Link>
              <Nav.Link 
                href="#contact" 
                className={({ isActive }) => isActive ? 'active' : ''}
                onClick={(e) => scrollToSection(e, '#contact')}
              >
                Contact Me
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
      <div className="main-content">
      <section className="hero" style={{ margin: 0, padding: 0, position: 'relative', top: 0 }}>
        <div className="hero-content">
          <h1>D.B. (Brian) Shreve</h1>
          <div className="skills">
            <span>Web Engineering</span>
            <span>AI Systems Integration</span>
            <span>Systems Innovator</span>
            <span>Web-AI Apps</span>
            <span>Consulting</span>
            <span>HTML5</span>
            <span>Next.js</span>
            <span>Node.js</span>
            <span>React</span>
          </div>
        </div>
      </section>

      <section id="about" className="section">
        <h3>About Me</h3>
        <h4>I'm a Data-Driven Innovator</h4>
        <p>I am Brian Shreve, a data-driven innovator with more than 30 years of experience in Electrical and Software Engineering. My career bridges world-class broadcast systems and modern web technologies, combining technical precision with creative problem-solving.</p>
        <p>I wrote my first program in BASIC on my VIC20 Computer at ten years-old, was immersed in Electrical Engineering from 1989, launched my first website in 1995 and thusly, have been Programming and/or Engineering, in one way or another, for much of my life. My background spans Broadcast Systems Design and Installation, Live Performance SV&C (Sound, Video and Communications Systems), Residential Audio, Visual, Security, Life Safety, Telephone and Data Systems, and Web Solutions of all types.</p>
        <p>My experience gives me the ability to integrate technology across industries and deliver results that stand the test of time.</p>
      </section>

      <section id="career" className="section">
        <div className="section-content">
          <h3>Career Highlights</h3>
          <h4>Proven Expertise Across Industries</h4>
          <br/>&nbsp;<br/>
          <div className="services-grid">
            <div className="service-card">
              <div className="card-content">
                <h4>Disney Hall Project</h4>
                <p>Contributed 3D Renderings & Schematics for the Live Performance Sound, Video, and Communications Systems at Disney Hall, Los Angeles, CA.</p>
              </div>
            </div>
            <div className="service-card">
              <div className="card-content">
                <h4>Emmy Award Winner</h4>
                <p>Part of an Emmy Award-winning Engineering Team for the 1998 Nagano Olympic Games Broadcast Systems.</p>
              </div>
            </div>
            <div className="service-card">
              <div className="card-content">
                <h4>Broadcast Engineering</h4>
                <p>15 years with Electrical Engineering Design-Build firms; building Television & Radio Broadcast Facilities, Post-production Studios, and Live Broadcast Systems.</p>
              </div>
            </div>
            <div className="service-card">
              <div className="card-content">
                <h4>Engineering Consultancy</h4>
                <p>5 years in Engineering Consultancy, specializing in 'Live Performance Sound, Video, and Communications Systems.'</p>
              </div>
            </div>
            <div className="service-card">
              <div className="card-content">
                <h4>Residential Systems</h4>
                <p>25 years designing and installing Residential Media, Video Distribution & Security Systems.</p>
              </div>
            </div>
            <div className="service-card">
              <div className="card-content">
                <h4>Web Development</h4>
                <p>Over 25 years of experience in web development, creating custom solutions for businesses and organizations.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="focus" className="section">
        <h3>Today's Focus</h3>
        <h4>Web Engineering & Hosting Solutions</h4>
        <br/>&nbsp;<br/>
        <p className="left-align">I now concentrate on Web Engineering, Hosting, and Programming Solutions for individuals, businesses, and organizations. Each solution is tailored, precise, and built with the same attention to detail that defined my work related to Electrical Engineering.</p>
        <div className="freelancer-section">
          <p>As a Freelancer, I provide:</p>
          <div className="services-grid">
            <div className="service-card">
              <div className="card-content">
                <h4>Custom Website Design & Development</h4>
                <p>Tailored web solutions that align with your brand and business goals.</p>
              </div>
            </div>
            <div className="service-card">
              <div className="card-content">
                <h4>AI Systems Development & Integration</h4>
                <p>Intelligent solutions to automate and enhance your operations.</p>
              </div>
            </div>
            <div className="service-card">
              <div className="card-content">
                <h4>Reliable Web Hosting Solutions</h4>
                <p>Fast, secure, and scalable hosting for your online presence.</p>
              </div>
            </div>
            <div className="service-card">
              <div className="card-content">
                <h4>App Development & Automation</h4>
                <p>Custom applications to streamline your business processes.</p>
              </div>
            </div>
            <div className="service-card">
              <div className="card-content">
                <h4>Systems Integration & Consulting</h4>
                <p>Seamlessly connect your existing systems and workflows.</p>
              </div>
            </div>
            <div className="service-card">
              <div className="card-content">
                <h4>Technical Strategy & Support</h4>
                <p>Expert guidance and ongoing support for your technical needs.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <h3>What My Clients Say</h3>
        <TestimonialsCarousel />
      </section>

      <section id="personal" className="section">
        <h3>Personal Side</h3>
        <h4>Creativity Beyond the Code</h4>
        <div className="personal-content">
          <div className="personal-intro">
            <p>Many other things interest me, the most of which are:</p>
          </div>
          
          <div className="passions-grid">
            <div className="passion-card">
              <h5>Music & Guitar</h5>
              <p>Playing guitar and exploring musical creativity</p>
            </div>
            
            <div className="passion-card">
              <h5>Dogs</h5>
              <p>Animal lover and dog enthusiast</p>
            </div>
            
            <div className="passion-card">
              <h5>Motorsports</h5>
              <p>Passionate about all forms of racing</p>
            </div>
            
            <div className="passion-card">
              <h5>Culinary Arts</h5>
              <p>Cooking and savoring great food</p>
            </div>
          </div>
        </div>
      </section>

      <section id="contact" className="section">
        <h3>Contact Me</h3>
        <h4><strong>Let's Build Something Great.</strong></h4>
        <div className="contact-content">
          <p>Looking for a trusted web engineer with decades of proven technical expertise?</p>
          <p>Try me out.</p>
          <div className="email-container">
            <button 
              type="button" 
              className="email-link" 
              title="Click to reveal email"
              onClick={handleEmailClick}
              style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', font: 'inherit', cursor: 'pointer' }}
            >
              {email || 'Loading...'}
            </button>
            <div className="email-instruction">(click twice, but only if you're HUMAN)</div>
          </div>
        </div>
      </section>

      </div>
      
      <footer className="App-footer">
        <div className="footer-content">
          <nav className="footer-nav">
            <a href="#about">About Me</a>
            <a href="#career">Career Highlights</a>
            <a href="#focus">Today's Focus</a>
            <a href="#personal">Personal Side</a>
            <a href="#contact">Contact Me</a>
          </nav>
          <p className="copyright">© 2025 D.B. (Brian) Shreve. All rights reserved.</p>
        </div>
      </footer>
      <BackToTopButton />
    </div>
  );
}

export default App;
